import { addBreadcrumb, captureException } from '@sentry/react';

import type { Breadcrumb } from '@sentry/react';

class RequestError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RequestError';
  }
}

/**
 * Error that client doesn't need to report to Sentry
 */
class RequestErrorServerSideError extends RequestError {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RequestErrorServerSideError';
  }
}

/**
 * Error that is specific to connection issue
 */
class RequestErrorConnectionIssueError extends RequestError {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RequestErrorConnectionIssueError';
  }
}

/**
 * Error that client needs to report to Sentry
 */
class RequestErrorClientSideError extends RequestError {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RequestErrorClientSideError';
  }
}

/**
 * Error where status code is 401, often means that the prospect has already applied
 */
class UnauthorizedError extends RequestError {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Error where status code is 409, indicates a conflict with the product version.
 */
class ConflictError extends RequestError {
  productVersion?: string;

  constructor(message?: string, productVersion?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'ConflictError';
    this.productVersion = productVersion;
  }
}

const isUnauthorizedError = (error: unknown): error is UnauthorizedError => {
  return error instanceof UnauthorizedError;
};

const isConflictError = (error: unknown): error is ConflictError => {
  return error instanceof ConflictError;
};

const SENTRY_TAGS = {
  connectionIssue: { exception_type: 'connection-issue' },
};

type CommandErrorResponse = {
  status: number;
  data?: string | unknown;
};

type AxiosError = Error & { response?: CommandErrorResponse };

// Just a hack to avoid importing the whole axios library just to use axios.isAxiosError
const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as { isAxiosError?: boolean }).isAxiosError === true;
};

/**
 * Wrap the command function with try clause and cast the errors into RequestErrorServerSideError or RequestErrorClientSideError conditionally.
 * If none of the conditions meet, throw the error as it is.
 * This function assumes that the command function uses Axios and will throw Axios error if necessary.
 */
const withCommandErrorCast = <T>(commandType: string, func: (...args: unknown[]) => Promise<T>): Promise<T> => {
  const formatBreadcrumbData = (error: AxiosError) => {
    let message;
    try {
      message = typeof error.response?.data === 'string' ? error.response?.data : JSON.stringify(error.response?.data);
    } catch {
      message = `typeof error.response?.data: ${typeof error.response?.data}`;
    }

    return {
      commandType,
      status: error.response?.status,
      message,
    };
  };

  const formatClientSideErrorMessage = (error: AxiosError) => `${error.message} on command: ${commandType}`;

  return withAxiosErrorCast(formatBreadcrumbData, formatClientSideErrorMessage)(func);
};

const withAxiosErrorCast =
  (
    formatBreadcrumbData: (error: AxiosError) => Breadcrumb['data'] = () => ({}),
    formatClientSideErrorMessage: (error: AxiosError) => string = (error) => error.message,
  ) =>
  <T>(func: (...args: unknown[]) => Promise<T>) => {
    return func().catch((error) => {
      if (isAxiosError(error)) {
        addBreadcrumb({
          category: 'debug',
          message: 'Axios error thrown',
          data: formatBreadcrumbData(error),
          level: 'debug',
        });

        if (error.response?.status === 500) {
          throw new RequestErrorServerSideError(error.message, { cause: error });
        }

        if (error.message.match(/timeout of \d+ms exceeded/) !== null) {
          throw new RequestErrorConnectionIssueError(error.message, { cause: error });
        }

        if (error.response?.status === 0) {
          throw new RequestErrorConnectionIssueError(error.message, { cause: error });
        }

        if (error.response?.status === 401) {
          throw new UnauthorizedError(error.message, { cause: error });
        }

        if (error.response?.status === 409) {
          const data = error.response?.data as { data?: { productVersion?: string } };
          throw new ConflictError(error.message, data?.data?.productVersion, { cause: error });
        }

        throw new RequestErrorClientSideError(formatClientSideErrorMessage(error));
      }

      throw error;
    });
  };

// Note that this function intentionally ignores server error
const handleRequestErrorOrThrow = (error: unknown) => {
  if (error instanceof UnauthorizedError) {
    return Promise.reject(error);
  }

  if (error instanceof ConflictError) {
    // Let conflict errors be handled by the caller
    return Promise.reject(error);
  }

  if (error instanceof RequestErrorClientSideError) {
    captureException(error);
    return Promise.resolve();
  }

  if (error instanceof RequestErrorConnectionIssueError) {
    captureException(error, { tags: SENTRY_TAGS.connectionIssue, level: 'log' });
    return Promise.resolve();
  }

  throw error;
};

export {
  RequestErrorServerSideError,
  RequestErrorClientSideError,
  ConflictError,
  isUnauthorizedError,
  isConflictError,
  withCommandErrorCast,
  withAxiosErrorCast,
  handleRequestErrorOrThrow,
};
